import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "gutter-web-design-colorado",
  metaTitle: "Gutter Contractor Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom gutter web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Gutter Contractor Web Design Colorado",
  eyebrow: "Gutter Contractor Web Design — Colorado",
  h1: (
    <>
      Gutter Contractor <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded gutter websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Gutter websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first gutter sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Gutter Contractor web design in Colorado",
    body: "Gutter Contractor web design is the practice of building conversion-focused, mobile-first websites for gutter businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with instant quote above the fold." },
    { title: "Service pages per offering", body: "Install, repair, cleaning, gutter guards, downspout, copper — each ranks independently." },
    { title: "Instant quote calculator", body: "Linear-foot calculator that quotes and books in under 60 seconds." },
    { title: "Insurance claim guidance", body: "CO hail/wind claim content captures research traffic." },
    { title: "Sticky click-to-call", body: "Phone on every scroll." },
    { title: "Schema and SEO at launch", body: "LocalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Cleaning and install are different searches", body: "Gutter cleaning is a recurring service, install is one-time — different pages, different funnels." },
    { heading: "Gutter guards are the high-margin add-on", body: "LeafFilter-style competitors charge premium — dedicated gutter guard pages capture that demand." },
    { heading: "Storm claims drive install demand", body: "CO hail and wind claims drive install spikes — claim-ready messaging captures the demand." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a gutter website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a gutter website?", a: "Typical timeline is 4–6 weeks for a 12–20 page gutter site with full service and service-area pages. Larger multi-location gutter businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs gutter businesses?", a: "No — we build gutter sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Gutter Contractor Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for gutter businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/gutter-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
